import { useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { type AgentList, AgentScope, agentListPath } from "~/services/agents";

/**
 * Resolves the UUID of the SYSTEM agent with the given `name` (e.g.
 * "dataset-assistant") by scanning `GET /agents`.
 *
 * The dataset assistant creates conversations with only an `app_id`; the
 * conversations service deliberately leaves `conversation.agent_id` NULL and
 * lets the orchestrator resolve the agent from `app_id` at turn time. The
 * connector picker, however, needs the agent UUID up front to fetch the
 * agent's granted connectors — so we resolve the backing SYSTEM agent by name
 * here and use it as the conversation's effective agent id.
 *
 * Returns null while the list is loading and when no SYSTEM agent matches.
 */
export function useSystemAgentId(name: string | null): string | null {
  const { data } = useFetcher<AgentList>(name ? agentListPath() : null);

  return useMemo(() => {
    if (!name || !data) {
      return null;
    }
    const match = data.items.find(
      (agent) => agent.name === name && agent.scope === AgentScope.SYSTEM
    );
    return match?.id ?? null;
  }, [data, name]);
}
